import {useState, useEffect, useContext} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose} from "@fortawesome/free-solid-svg-icons";
import {FormProvider, useForm} from "react-hook-form";

// Contexts
import {AuthContext} from "../../contexts/auth.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import usePrevious from "../../hooks/usePrevious.js";
import useVisibilityConflicts from "../../hooks/useVisibilityConflicts.js";

// Components
import Modal from "../../components/Modal.js";
import CheckBox from "../../components/CheckBox.js";
import Help from "../../components/Help.js";
import SearchSelect from "../../components/SearchSelect.js";
import {InputRadioGroup} from "../../components/form/FormInputs.js";
import VisibilityConflictWarning from "./VisibilityConflictWarning.js";

// Style
import {voice} from "../../style/components/typography.js";
import {flex} from "../../style/components/mixins.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {
  Abbr,
  Button,
  Heading,
  Label,
  Inline,
  Small,
  Form,
  Error,
  Text
} from "../../style/components/general.js";

/** Checksheet API uses `invited`; facility-users / search use `isInvited`. */
const withInviteStatus = members =>
  (members || []).map(member => {
    if (!member?.publicId) return member;
    const {invited, ...rest} = member;
    return {
      ...rest,
      isInvited: member.isInvited ?? invited
    };
  });

const withEmailOptionLabel = user => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  let optionLabel = name;
  if (user.email) optionLabel = name ? `${name} (${user.email})` : user.email;
  return {...user, optionLabel};
};

const ModalMemberRestrict = ({
  visible,
  setVisible,
  restrictTo = [],
  setRestrictTo,
  external = false,
  setExternal,
  allowExternal = false,
  facilityId,
  checksheetRestrictions = []
}) => {
  const {roles} = useContext(AuthContext) || {};

  const {api: apiUser} = useApi("facility-users");
  const {api: apiRole} = useApi("user-roles");

  const [selectedMembers, setSelectedMembers] = useState(() => withInviteStatus(restrictTo));
  const [selectedMemberError, setSelectedMemberError] = useState();
  const [selectedExternal, setSelectedExternal] = useState(external);

  const conflicts = useVisibilityConflicts(selectedMembers, checksheetRestrictions, facilityId);
  const [confirmConflicts, setConfirmConflicts] = useState(false);

  useEffect(() => {
    if (!conflicts.length) setConfirmConflicts(false);
  }, [conflicts]);

  useEffect(() => {
    if (!allowExternal) setSelectedExternal(false);
  }, [allowExternal]);

  const available = roles?.filter(
    curr =>
      curr?.permissions?.resource?.byId?.facility_checksheet_record?.view === "enabled" &&
      curr?.permissions?.resource?.byId?.facility_checksheet_record?.create === "enabled"
  );

  const [searchResults, setSearchResults] = useState([]);

  // Start in the saved mode; switching modes after mount converts the selection.
  const form = useForm({
    defaultValues: {
      mode: restrictTo?.length && !restrictTo[0]?.publicId ? "Role" : "User"
    }
  });

  const {watch, handleSubmit} = form;

  const watchMode = watch("mode");

  const prevMode = usePrevious(watchMode);

  const handleSearchRole = query => {
    if (query) {
      const lower = query.toLowerCase();
      const tempResults = available.filter(
        option =>
          option.label.toLowerCase().includes(lower) &&
          !selectedMembers?.map(role => role.name).includes(option.name)
      );
      tempResults.sort(
        (a, b) => a.label.toLowerCase().indexOf(lower) - b.label.toLowerCase().indexOf(lower)
      );
      setSearchResults(tempResults);
    } else
      setSearchResults(
        available.filter(role => !selectedMembers?.map(r => r.name).includes(role.name))
      );
  };

  const handleSearchUser = query => {
    const filter = {
      Omit: selectedMembers?.map(({publicId}) => publicId),
      showHidden: true
    };

    if (query) filter.Search = query;

    const params = {
      facilityId,
      noTest: true,
      limit: query ? 5 : 20,
      filter: JSON.stringify(filter)
    };

    apiUser.callGet("", params).then(({status, data}) => {
      if (status === 200 && data) setSearchResults(data.map(withEmailOptionLabel));
    });
  };

  useEffect(() => {
    if (prevMode && prevMode !== watchMode) {
      if (watchMode === "User" && selectedMembers?.length) {
        const params = {
          facilityId,
          noTest: true,
          filter: JSON.stringify({
            Roles: selectedMembers.map(({name}) => name)
          }),
          limit: "all"
        };
        apiUser.callGet("", params).then(({status, data}) => {
          if (status === 200 && data)
            setSelectedMembers(
              data.map(({publicId, email, firstName, lastName, isInvited}) => ({
                publicId,
                email,
                firstName,
                lastName,
                isInvited
              }))
            );
        });
      } else if (watchMode === "Role" && selectedMembers?.length) {
        const params = {
          filter: JSON.stringify({
            Users: selectedMembers.map(({publicId}) => publicId)
          })
        };
        apiRole.callGet("", params).then(({status, data}) => {
          if (status === 200 && data)
            setSelectedMembers(
              data.map(({label, name, id}) => ({
                label,
                name,
                id
              }))
            );
        });
      } else {
        setSelectedMembers([]);
      }
    }
  }, [prevMode, watchMode, facilityId, selectedMembers, apiUser, apiRole]);

  const handleSave = ({confirmed = false} = {}) => {
    const targets = selectedMembers?.filter(({isInvited}) => !isInvited);
    if (selectedExternal && !(selectedMembers?.length > 0)) {
      setSelectedMemberError(
        "Assign a replacement before removing the last assignee of an External checksheet. Removing the only assignee on an External checksheet is not allowed."
      );
    } else if (targets?.length > 0 && watchMode === "User") {
      setSelectedMemberError(
        `All users must be invited for assignment! ${targets
          .map(({firstName, lastName}) => `${firstName} ${lastName}`)
          .join(", ")} ha${targets?.length > 1 ? "ve" : "s"} not been invited.`
      );
    } else if (conflicts.length > 0 && !confirmed) {
      setConfirmConflicts(true);
    } else {
      setRestrictTo(selectedMembers);
      if (setExternal)
        setExternal(allowExternal && selectedMembers?.length ? selectedExternal : false);
      setVisible(false);
    }
  };

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <HeaderWrapper>
        <ModalTitle>Checksheet Visibility Restriction</ModalTitle>&nbsp;
        <Help title="Restrict checksheet visibility">
          Limits who can see this checksheet at all &ndash; only the listed users or roles (and
          admins) will see it on the Records tab. Leave empty to keep it visible to everyone with
          access to the facility.
        </Help>
      </HeaderWrapper>

      <Menu>
        <Wrapper>
          <FormProvider {...form}>
            <Form onSubmit={handleSubmit(handleSave)} noValidate>
              <RadioWrapper>
                {(available?.length > 0 || watchMode === "Role") && (
                  <InputRadioGroup name="mode" options={["User", "Role"]} />
                )}
              </RadioWrapper>
              {watchMode === "Role" ? (
                <SearchSelect
                  placeholder="Find role(s)..."
                  results={searchResults}
                  setResults={setSearchResults}
                  search={handleSearchRole}
                  add={({id, name, label}) => {
                    setSelectedMembers(prev => {
                      if (prev && !prev.map(curr => curr.id).includes(id))
                        return [...prev, {id, name, label}];
                      return prev ?? [{id, name, label}];
                    });
                  }}
                  showAll
                />
              ) : (
                <SearchSelect
                  placeholder="Find user(s)..."
                  results={searchResults}
                  setResults={setSearchResults}
                  search={handleSearchUser}
                  add={({publicId, email, firstName, lastName, isInvited}) => {
                    setSelectedMembers(prev => {
                      if (prev && !prev.map(curr => curr.publicId).includes(publicId))
                        return [...prev, {publicId, email, firstName, lastName, isInvited}];
                      return prev ?? [{publicId, email, firstName, lastName, isInvited}];
                    });
                  }}
                  showAll
                />
              )}
              <Inline>
                {selectedMembers?.map(
                  ({label: roleLabel, firstName, lastName, publicId, email, id: roleId}) => {
                    const label = firstName ? `${firstName} ${lastName}` : roleLabel;
                    const id = roleId || publicId;
                    const tooltip = email ? `${label} (${email})` : label;
                    return (
                      <SelectedRole key={label}>
                        <Abbr title={tooltip.toUpperCase()}>{label.toUpperCase()}</Abbr>
                        <IconButton
                          type="button"
                          onClick={() => {
                            setSelectedMembers(prev => {
                              let toRemove;
                              prev.map((role, index) => {
                                if (
                                  (role.id && role.id === id) ||
                                  (role.publicId && role.publicId === id)
                                )
                                  toRemove = index;
                              });
                              const newRoles = [...prev];
                              newRoles.splice(toRemove, 1);
                              return newRoles;
                            });
                          }}>
                          <FontAwesomeIcon icon={faClose} />
                        </IconButton>
                      </SelectedRole>
                    );
                  }
                )}
              </Inline>
              {setExternal && (selectedMembers?.length > 0 || selectedExternal) && (
                <External>
                  <CheckBox
                    testId="restrict.external"
                    initial={allowExternal && external}
                    handleCheck={state => setSelectedExternal(state)}
                    triggerCheck={allowExternal ? selectedExternal : false}
                    disabled={!allowExternal}
                  />
                  <Small>External (access via email only)</Small>
                  <Help title="External checksheet">
                    External checksheets are completed via email on the checksheet schedule. A
                    frequency other than None is required.
                  </Help>
                </External>
              )}
              <br />
              <VisibilityConflictWarning conflicts={conflicts} />
              {selectedMemberError && <Error>{selectedMemberError}</Error>}
              {confirmConflicts ? (
                <>
                  <Text>Save anyway?</Text>
                  <Inline>
                    <Button type="button" onClick={() => handleSave({confirmed: true})}>
                      Save anyway
                    </Button>
                    <Button type="button" onClick={() => setConfirmConflicts(false)}>
                      Back
                    </Button>
                  </Inline>
                </>
              ) : (
                <Button type="submit">Save</Button>
              )}
            </Form>
          </FormProvider>
        </Wrapper>
      </Menu>
    </Modal>
  );
};

ModalMemberRestrict.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  restrictTo: PropTypes.arrayOf(PropTypes.any),
  setRestrictTo: PropTypes.func.isRequired,
  external: PropTypes.bool,
  setExternal: PropTypes.func,
  allowExternal: PropTypes.bool,
  facilityId: PropTypes.number.isRequired,
  checksheetRestrictions: PropTypes.arrayOf(PropTypes.any)
};

// Style Overrides
const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: ${pad}px 0;
`;

const ModalTitle = styled(Heading)`
  margin: 0;
`;

const Menu = styled.div`
  position: relative;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  width: 100%;
  padding: ${pad}px;
  margin-bottom: ${pad}px;

  ${Label} {
    ${voice.quiet};
    margin-bottom: ${pad / 2}px;
  }

  ${Inline} {
    flex-wrap: wrap;
    margin-top: ${pad / 2}px;
    gap: ${pad / 2}px;
  }
`;

const Wrapper = styled.div`
  margin-top: ${pad}px;
`;

const SelectedRole = styled.div`
  ${flex("row", "nowrap", "space-between", "center")};
  width: min-content;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  background-color: ${({theme}) => theme.secondary};
  padding: ${pad / 2}px ${pad}px;

  ${Abbr} {
    ${voice.quiet};
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const IconButton = styled(Button)`
  ${voice.small};
  background-color: transparent;
  width: min-content;
  padding: 0;
  margin-left: ${pad / 2}px;

  svg {
    fill: ${({theme}) => theme.tertiary};
  }
`;

const External = styled(Inline)`
  span {
    color: ${({theme}) => theme.secondary};
  }
`;

const RadioWrapper = styled.div`
  margin-top: -${pad}px;
`;

export default ModalMemberRestrict;
